import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import {
  doc, getDoc, collection, getDocs, query, where
} from "firebase/firestore";
import { Card } from "@mui/material";

/* ================= COLORS ================= */
const NAVY = "#0a2540";
const NAVY_LIGHT = "#1e3a8a";
const GOLD = "#d4af37";
const GOLD_SOFT = "#f5e6b3";
const COLORS = [GOLD, NAVY];


function Dashboard() {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [propertyStatus, setPropertyStatus] = useState(null);
  const [propertyName, setPropertyName] = useState("");

  const [dynamicBarData, setDynamicBarData] = useState([]);
  const [dynamicPieData, setDynamicPieData] = useState([]);

  const [summary, setSummary] = useState({
    today: 0,
    tomorrow: 0,
    month: 0,
    future: 0,
    live: 0,
    available: 0,
    cleaning: 0,
    totalRooms: 0
  });

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(user => setUserData(user));
    return () => unsub();
  }, []);

  /* ================= ROLE + DASHBOARD LOAD ================= */
  useEffect(() => {
    if (!userData) return;

    const loadDashboard = async () => {
      try {
        const roleSnap = await getDoc(doc(db, "authroles", userData.uid));

        if (!roleSnap.exists() || !roleSnap.data().propertyId) {
          setPropertyStatus("no-property");
          return;
        }

        const propertyId = roleSnap.data().propertyId;

        const propSnap = await getDoc(doc(db, "properties", propertyId));
        if (propSnap.exists()) {
          const p = propSnap.data();
          setPropertyName(p.hotelname || "My Hotel");
          setPropertyStatus(p.status || "Active");

          if (p.status === "Active") {
            await fetchDashboardAnalytics(propertyId);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [userData]);

  /* ================= ANALYTICS ================= */
  const fetchDashboardAnalytics = async (propertyId) => {
    const today = moment().format("YYYY-MM-DD");
    const tomorrow = moment().add(1, "day").format("YYYY-MM-DD");
    const monthStart = moment().startOf("month").format("YYYY-MM-DD");
    const last7Days = moment().subtract(6, "days").format("YYYY-MM-DD");

    const [
      todaySnap,
      tomorrowSnap,
      monthSnap,
      futureSnap,
      allResSnap,
      roomsSnap
    ] = await Promise.all([
      getDocs(query(collection(db, "onlinebookings"),
        where("propertyId", "==", propertyId),
        where("status", "==", "Paid"),
        where("checkindate", "==", today)
      )),
      getDocs(query(collection(db, "onlinebookings"),
        where("propertyId", "==", propertyId),
        where("status", "==", "Paid"),
        where("checkindate", "==", tomorrow)
      )),
      getDocs(query(collection(db, "onlinebookings"),
        where("propertyId", "==", propertyId),
        where("status", "==", "Paid"),
        where("checkindate", ">=", monthStart)
      )),
      getDocs(query(collection(db, "onlinebookings"),
        where("propertyId", "==", propertyId),
        where("status", "==", "Paid"),
        where("checkindate", ">", tomorrow)
      )),
      getDocs(query(collection(db, "reservations"),
        where("propertyId", "==", propertyId),
        where("checkindate", ">=", last7Days)
      )),
      getDocs(query(collection(db, "roominfos"),
        where("propertyId", "==", propertyId)
      ))
    ]);

    const rooms = roomsSnap.docs.map(d => d.data());
    const available = rooms.filter(r => r.status === "Available").length;
    const cleaning = rooms.filter(r => r.status === "Cleaning").length;
    const occupied = rooms.filter(r => r.status === "Booked").length;

    setSummary({
      today: todaySnap.size,
      tomorrow: tomorrowSnap.size,
      month: monthSnap.size,
      future: futureSnap.size,
      live: occupied,
      available,
      cleaning,
      totalRooms: roomsSnap.size
    });

    /* Bar chart */
    const map = {};
    for (let i = 6; i >= 0; i--) {
      map[moment().subtract(i, "days").format("DD MMM")] = 0;
    }
    allResSnap.forEach(d => {
      const key = moment(d.data().checkindate).format("DD MMM");
      if (map[key] !== undefined) map[key]++;
    });
    setDynamicBarData(Object.entries(map).map(([date, bookings]) => ({ date, bookings })));

    /* Pie chart */
    setDynamicPieData([
      { name: "Available", value: available, color: "#10b981" },
      { name: "Cleaning", value: cleaning, color: "#94a3b8" },
      { name: "Occupied", value: occupied, color: "#ef4444" }
    ]);
  };

  /* ================= UI STATES ================= */
  if (loading) {
    return <div className="p-5 text-center"><h5>Loading Dashboard...</h5></div>;
  }

  if (propertyStatus === "Pending") {
    return (
      <>
        <Menu />
        <div className="content-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
          <Card className="p-5 text-center shadow">
            <h4>Account Not Activated</h4>
            <p>{propertyName} is under verification</p>
          </Card>
        </div>
      </>
    );
  }

  /* ================= HELPER: SUMMARY CARDS DATA ================= */
  const SUMMARY_CARDS = [
    { title: "Today's Bookings", value: summary.today, icon: "fa-calendar-check", link: "#" },
    { title: "Tomorrow's Bookings", value: summary.tomorrow, icon: "fa-calendar-plus", link: "#" },
    { title: "This Month", value: summary.month, icon: "fa-chart-line", link: "#" },
    { title: "Future Bookings", value: summary.future, icon: "fa-forward", link: "#" },
    { title: "Live Reservations", value: summary.live, icon: "fa-bed", link: "#" },
    { title: "Available Rooms", value: summary.available, icon: "fa-door-open", link: "#" },
    { title: "Cleaning Rooms", value: summary.cleaning, icon: "fa-broom", link: "#" },
    { title: "All Rooms", value: summary.totalRooms, icon: "fa-hotel", link: "#" }
  ];


  /* ================= UI ================= */
  return (
    <div>
      <Menu />
      <div className="content-wrapper" style={{ padding: 20, background: "#f5f7fb" }}>

        <h4 className="mb-4" style={{ color: NAVY }}>
          {propertyName} Dashboard
        </h4>
        {/* MODERN SUMMARY CARDS */}
        <div className="row">
          {SUMMARY_CARDS.map((c, i) => (
            <div className="col-lg-3 col-6 mb-4" key={i}>
              <div
                onClick={() => navigate(c.link)}
                style={{
                  position: "relative",
                  borderRadius: 10,
                  padding: "10px 25px",
                  color: "#fff",
                  background: NAVY,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(255,255,255,0.1)"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow = "0 20px 30px rgba(0,0,0,0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 10px 20px rgba(0,0,0,0.1)";
                }}
              >
                <i className={`fas ${c.icon}`} style={{ position: "absolute", top: 20, right: 20, fontSize: 40, opacity: 0.15 }} />

                <div style={{ zIndex: 1 }}>
                  <h1 style={{ fontSize: "2.4rem", fontWeight: 800, margin: 0, letterSpacing: "-1px" }}>{c.value}</h1>
                  <p style={{ fontSize: 13, fontWeight: 500, opacity: 0.7, textTransform: "uppercase" }}>{c.title}</p>
                </div>

               
              </div>
            </div>
          ))}
        </div>

        {/* DYNAMIC CHARTS */}
        <div className="row mt-2 mb-4">
          <div className="col-lg-8 mb-4">
            <Card
              style={{
                padding: 30,
                borderRadius: 24,
                border: "none",
                background: "#ffffff",
                boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
                transition: "transform 0.3s ease",
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h6 style={{ fontWeight: 800, color: NAVY, margin: 0 }}>
                    Weekly Booking Trend
                  </h6>
                  <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>
                    Live performance for the last 7 days
                  </p>
                </div>
                <i className="fas fa-ellipsis-h" style={{ color: "#cbd5e1", cursor: "pointer" }}></i>
              </div>

              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={dynamicBarData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    {/* MODERN GRADIENT FOR BARS */}
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1e3a8a" stopOpacity={1} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.6} />
                    </linearGradient>

                    {/* GLOW EFFECT FOR HOVER */}
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* SUBTLE DASHED GRID */}
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#64748b", fontSize: 12, fontWeight: 500 }}
                    dy={10}
                  />

                  <YAxis axisLine={false} tickLine={false} hide />

                  <Tooltip
                    cursor={{ fill: "#f8fafc", radius: 10 }}
                    contentStyle={{
                      borderRadius: "12px",
                      border: "none",
                      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                      padding: "10px 14px",
                    }}
                    itemStyle={{ fontWeight: 700, color: NAVY }}
                    labelStyle={{ color: "#64748b", marginBottom: 4 }}
                  />

                  <Bar
                    dataKey="bookings"
                    fill="url(#barGradient)"
                    radius={[10, 10, 4, 4]} // Rounded top and slightly rounded bottom
                    barSize={32}

                    /* ANIMATION SETTINGS */
                    isAnimationActive={true}
                    animationBegin={200}
                    animationDuration={1500}
                    animationEasing="ease-out"

                    /* INTERACTIVE HOVER STATE */
                    activeBar={{
                      filter: "url(#glow)",
                      fill: "#1e40af",
                      stroke: "#d4af37", // Added a thin Golden stroke on hover
                      strokeWidth: 1
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <div className="col-lg-4 mb-4">
            <Card
              style={{
                padding: 30,
                borderRadius: 24,
                border: "none",
                background: "#ffffff",
                boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
              }}
            >
              {/* HEADER */}
              <div className="mb-2">
                <h6 style={{ fontWeight: 800, color: NAVY, margin: 0 }}>Room Inventory</h6>
                <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>Current occupancy status</p>
              </div>

              <div style={{ position: "relative", width: "100%", height: 280 }}>
                {/* CENTRAL METRIC DATA */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    zIndex: 1
                  }}
                >
                  <h2 style={{ margin: 0, fontWeight: 800, color: NAVY }}>{summary.totalRooms}</h2>
                  <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", color: "#94a3b8", fontWeight: 700 }}>Total Rooms</p>
                </div>

                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip
                      contentStyle={{
                        borderRadius: "12px",
                        border: "none",
                        boxShadow: "0 10px 15px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Pie
                      data={dynamicPieData}
                      dataKey="value"
                      innerRadius={80} // Thinner ring for modern look
                      outerRadius={105}
                      paddingAngle={10} // Increased gap between segments
                      cornerRadius={40} // Rounded ends for the "pill" look
                      stroke="none"
                      /* ANIMATION */
                      isAnimationActive={true}
                      animationBegin={300}
                      animationDuration={1400}
                      animationEasing="ease-out"
                    >
                      {dynamicPieData.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={entry.color}
                          style={{
                            filter: `drop-shadow(0px 4px 10px ${entry.color}44)`, // Dynamic subtle glow
                            cursor: 'pointer'
                          }}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* MODERN LEGEND */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 15,
                  flexWrap: "wrap",
                  background: "#f8fafc",
                  padding: "12px",
                  borderRadius: "16px"
                }}
              >
                {dynamicPieData.map((d, i) => (
                  <div key={i} className="d-flex align-items-center">
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: d.color,
                        marginRight: 6
                      }}
                    />
                    <span style={{ fontSize: 11, fontWeight: 700, color: NAVY }}>
                      {d.value} <span style={{ color: "#94a3b8", fontWeight: 500 }}>{d.name}</span>
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>



      </div>
    </div>
  );
}

export default Dashboard;
